import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquarePlus, Send, Trash2, Loader2, ExternalLink, Sparkles, FlaskConical, Building2, Globe2, Play, ChevronLeft, ChevronRight, Compass, ListChecks, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import { DiscoveryMessage } from '@/components/copilot/DiscoveryMessage';

const API = '/api';

const ToolBadge = ({ tool }) => {
  if (!tool || tool === 'none') return null;
  const map = {
    search_assets: { icon: FlaskConical, label: 'Assets', color: 'bg-violet-50 text-violet-700 border-violet-200' },
    search_companies: { icon: Building2, label: 'Companies', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    web_search: { icon: Globe2, label: 'Web', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    fetch_brief: { icon: Sparkles, label: 'Past Brief', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    discover_universe: { icon: Compass, label: 'BD Discovery', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    refine_universe: { icon: Compass, label: 'Refined universe', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  };
  const c = map[tool];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c.color}`} data-testid={`tool-badge-${tool}`}>
      <Icon className="w-2.5 h-2.5" /> {c.label}
    </span>
  );
};

const Citation = ({ c, navigate }) => {
  const onClick = (e) => {
    e.preventDefault();
    if (c.kind === 'web' && c.url) window.open(c.url, '_blank');
    if (c.kind === 'asset' && c.company_id) navigate(`/biotech/${c.company_id}`);
    if (c.kind === 'company' && c.company_id) {
      if (c.company_kind === 'pharma') navigate(`/company-profiles/${c.company_id}`);
      else navigate(`/biotech/${c.company_id}`);
    }
  };
  const icon = c.kind === 'web' ? <ExternalLink className="w-3 h-3" /> :
               c.kind === 'asset' ? <FlaskConical className="w-3 h-3" /> :
               <Building2 className="w-3 h-3" />;
  return (
    <button
      onClick={onClick}
      className="inline-flex items-start gap-1.5 text-xs px-2.5 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md text-slate-700 max-w-full text-left transition-colors"
      title={c.url || c.subtitle || ''}
      data-testid="citation-chip"
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="truncate">
        <span className="font-medium">{c.title}</span>
        {c.subtitle && <span className="text-slate-500"> · {c.subtitle}</span>}
      </span>
    </button>
  );
};

const PipelineSuggestion = ({ suggestion, onRun }) => {
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agents/pharma-buyers`, { withCredentials: true })
      .then(r => {
        const list = r.data || [];
        setBuyers(list);
        // Best-effort match if hint provided
        const hint = (suggestion?.buyer_hint || '').toLowerCase();
        if (hint) {
          const match = list.find(b => b.name.toLowerCase().includes(hint));
          if (match) setSelectedBuyer(match.id);
        }
      })
      .catch(() => {});
  }, [suggestion]);

  const run = async () => {
    if (!selectedBuyer) { toast.error('Pick a pharma buyer'); return; }
    setRunning(true);
    try {
      const res = await axios.post(`${API}/copilot/run-pipeline`, {
        buyer_id: selectedBuyer,
        workstreams: ['all'],
      }, { withCredentials: true });
      toast.success('Pipeline started — track in Intelligence Hub');
      if (onRun) onRun(res.data.run_id);
    } catch {
      toast.error('Failed to start pipeline');
    }
    setRunning(false);
  };

  return (
    <Card className="border-sky-200 bg-sky-50/50 mt-3" data-testid="pipeline-suggestion">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <Play className="w-3.5 h-3.5 text-sky-600" />
          <p className="text-xs font-semibold text-slate-800">Run full Intelligence pipeline on these assets</p>
        </div>
        {suggestion.rationale && <p className="text-[11px] text-slate-600 mb-2">{suggestion.rationale}</p>}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedBuyer}
            onChange={(e) => setSelectedBuyer(e.target.value)}
            className="text-xs px-2 py-1.5 border border-slate-200 rounded bg-white flex-1 min-w-[180px]"
            data-testid="pipeline-buyer-select"
          >
            <option value="">Select pharma buyer…</option>
            {buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <Button size="sm" onClick={run} disabled={!selectedBuyer || running} className="bg-slate-900 text-white hover:bg-slate-800 text-xs h-8" data-testid="run-pipeline-from-chat">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            {running ? 'Starting…' : 'Run'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Message = ({ m, navigate, isLatestDiscovery, disco, handlers }) => {
  const isUser = m.role === 'user';
  const isDiscovery = !isUser && (m.discovery || m.discovery_status);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.role}`}>
      <div className={`${isDiscovery ? 'w-full' : 'max-w-[85%]'} rounded-2xl px-4 py-3 ${isUser ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
        {!isUser && m.tool && <div className="mb-2"><ToolBadge tool={m.tool} /></div>}
        {isUser ? (
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-white">{m.content}</div>
        ) : (
          <div className="prose prose-sm prose-slate max-w-none text-sm leading-relaxed
            prose-headings:font-bold prose-headings:text-slate-900 prose-headings:mt-3 prose-headings:mb-1.5
            prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
            prose-p:my-1.5 prose-p:text-slate-700
            prose-strong:text-slate-900 prose-strong:font-semibold
            prose-ul:my-1.5 prose-ul:pl-5 prose-li:my-0.5 prose-li:text-slate-700
            prose-ol:my-1.5 prose-ol:pl-5
            prose-code:text-[12px] prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
            prose-a:text-sky-700 prose-a:no-underline hover:prose-a:underline">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
          </div>
        )}
        {isDiscovery && <DiscoveryMessage m={m} isLatest={isLatestDiscovery} disco={disco} handlers={handlers} navigate={navigate} />}
        {!isUser && Array.isArray(m.citations) && m.citations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Sources</p>
            <div className="flex flex-wrap gap-1.5">
              {m.citations.map((c, i) => <Citation key={i} c={c} navigate={navigate} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const emptyDisco = () => ({ selected: new Set(), research: {}, researching: new Set(), crmContacts: {}, webResults: [], webLoading: false, adding: new Set(), jobStep: {}, jobProgress: {}, exporting: false });

export default function CopilotPage() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [disco, setDisco] = useState(emptyDisco);
  const [listDialog, setListDialog] = useState(false);
  const [listName, setListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const scrollRef = useRef(null);
  const pollers = useRef({});
  const navigate = useNavigate();

  const patchDisco = (fn) => setDisco(prev => ({ ...prev, ...fn(prev) }));

  const loadMessages = async (sid) => {
    const r = await axios.get(`${API}/copilot/sessions/${sid}`, { withCredentials: true });
    setMessages(r.data?.messages || []);
    return r.data?.messages || [];
  };

  const loadDiscoveryState = async (sid) => {
    try {
      const r = await axios.get(`${API}/copilot/discovery/${sid}`, { withCredentials: true });
      if (r.data?.active) patchDisco(() => ({ research: r.data.research || {}, webResults: r.data.web_results || [] }));
      else patchDisco(() => ({ research: {}, webResults: [] }));
    } catch { /* ignore */ }
  };

  const pollJob = (jobId, onDone) => {
    if (pollers.current[jobId]) return;
    pollers.current[jobId] = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/copilot/discovery/jobs/${jobId}`, { withCredentials: true });
        const j = r.data;
        patchDisco(p => ({ jobStep: { ...p.jobStep, [jobId]: j.step }, jobProgress: { ...p.jobProgress, [jobId]: j.progress } }));
        if (j.status === 'done' || j.status === 'error') {
          clearInterval(pollers.current[jobId]);
          delete pollers.current[jobId];
          onDone(j);
        }
      } catch { /* keep polling */ }
    }, 2500);
  };

  useEffect(() => () => Object.values(pollers.current).forEach(clearInterval), []);

  // Load sessions
  useEffect(() => {
    axios.get(`${API}/copilot/sessions`, { withCredentials: true })
      .then(r => setSessions(r.data || []))
      .catch(() => {});
  }, []);

  // Load messages when active session changes
  useEffect(() => {
    if (!activeId) { setMessages([]); setDisco(emptyDisco()); return; }
    setDisco(emptyDisco());
    loadMessages(activeId)
      .then(msgs => {
        msgs.filter(m => m.discovery_status === 'running' && m.discovery_job_id).forEach(m => pollJob(m.discovery_job_id, async () => { await loadMessages(activeId); await loadDiscoveryState(activeId); }));
      })
      .catch(() => toast.error('Failed to load session'));
    loadDiscoveryState(activeId);
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const newChat = async () => {
    try {
      const res = await axios.post(`${API}/copilot/sessions`, {}, { withCredentials: true });
      const s = res.data;
      setSessions(prev => [{ ...s, updated_at: new Date().toISOString() }, ...prev]);
      setActiveId(s.id);
      setMessages([]);
    } catch { toast.error('Failed to create session'); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API}/copilot/sessions/${id}`, { withCredentials: true });
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeId === id) { setActiveId(null); setMessages([]); }
    } catch { toast.error('Failed to delete'); }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    let sid = activeId;
    if (!sid) {
      try {
        const res = await axios.post(`${API}/copilot/sessions`, {}, { withCredentials: true });
        sid = res.data.id;
        setSessions(prev => [{ ...res.data, updated_at: new Date().toISOString() }, ...prev]);
        setActiveId(sid);
      } catch { toast.error('Failed to start chat'); return; }
    }
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, id: `tmp-${Date.now()}` }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/copilot/chat`, { session_id: sid, message: msg }, { withCredentials: true });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.content,
        tool: res.data.tool,
        citations: res.data.citations,
        pipeline_suggestion: res.data.pipeline_suggestion,
        discovery: res.data.discovery,
        discovery_status: res.data.discovery_status,
        discovery_job_id: res.data.discovery_job_id,
        id: res.data.id,
      }]);
      if (res.data.discovery_status === 'running' && res.data.discovery_job_id) {
        patchDisco(() => ({ selected: new Set(), webResults: [], research: {}, crmContacts: {} }));
        pollJob(res.data.discovery_job_id, async (j) => {
          await loadMessages(sid);
          await loadDiscoveryState(sid);
          if (j.status === 'error') toast.error(`Discovery failed: ${j.error || 'unknown error'}`);
        });
      }
      // Refresh sessions list so title updates
      const sres = await axios.get(`${API}/copilot/sessions`, { withCredentials: true });
      setSessions(sres.data || []);
    } catch {
      toast.error('Reply failed');
      setMessages(prev => prev.slice(0, -1));
    }
    setSending(false);
  };

  const handlers = {
    toggle: (key) => patchDisco(p => { const s = new Set(p.selected); s.has(key) ? s.delete(key) : s.add(key); return { selected: s }; }),
    selectMany: (keys, on) => patchDisco(p => { const s = new Set(p.selected); keys.forEach(k => on ? s.add(k) : s.delete(k)); return { selected: s }; }),
    research: async (key, mode) => {
      const tag = `${key}:${mode}`;
      patchDisco(p => ({ researching: new Set([...p.researching, tag]) }));
      try {
        const r = await axios.post(`${API}/copilot/discovery/${activeId}/research`, { key, mode }, { withCredentials: true });
        pollJob(r.data.job_id, async (j) => {
          await loadDiscoveryState(activeId);
          patchDisco(p => { const s = new Set(p.researching); s.delete(tag); return { researching: s }; });
          if (j.status === 'error') toast.error(`Research failed: ${j.error || ''}`);
        });
      } catch {
        toast.error('Could not start research');
        patchDisco(p => { const s = new Set(p.researching); s.delete(tag); return { researching: s }; });
      }
    },
    loadContacts: async (key) => {
      try {
        const r = await axios.get(`${API}/copilot/discovery/${activeId}/contacts`, { params: { key }, withCredentials: true });
        patchDisco(p => ({ crmContacts: { ...p.crmContacts, [key]: r.data.contacts || [] } }));
      } catch { toast.error('Could not load contacts'); }
    },
    expandWeb: async () => {
      patchDisco(() => ({ webLoading: true }));
      try {
        const r = await axios.post(`${API}/copilot/discovery/${activeId}/expand-web`, {}, { withCredentials: true });
        pollJob(r.data.job_id, async (j) => {
          await loadDiscoveryState(activeId);
          patchDisco(() => ({ webLoading: false }));
          if (j.status === 'error') toast.error(`Web search failed: ${j.error || ''}`);
          else toast.success(`${j.result?.new ?? 0} new companies found on the web`);
        });
      } catch { toast.error('Could not start web search'); patchDisco(() => ({ webLoading: false })); }
    },
    exportExcel: async () => {
      patchDisco(() => ({ exporting: true }));
      try {
        const keys = disco.selected.size ? [...disco.selected].join(',') : '';
        const r = await axios.get(`${API}/copilot/discovery/${activeId}/export.xlsx`, { params: keys ? { keys } : {}, responseType: 'blob', withCredentials: true });
        const name = (r.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/)?.[1] || 'ForesightRx_discovery.xlsx';
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(keys ? `Excel downloaded — ${disco.selected.size} shortlisted companies` : 'Excel downloaded — all companies in view');
      } catch (e) {
        let detail = e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || 'network error');
        try { const t = await e.response.data.text(); detail = JSON.parse(t).detail || detail; } catch { /* not json */ }
        toast.error(`Download failed — ${detail}`);
      }
      patchDisco(() => ({ exporting: false }));
    },
    addCompany: async (key) => {
      patchDisco(p => ({ adding: new Set([...p.adding, key]) }));
      try {
        const r = await axios.post(`${API}/copilot/discovery/${activeId}/add-company`, { key }, { withCredentials: true });
        patchDisco(p => ({ webResults: p.webResults.map(w => w.key === key ? { ...w, added_company_id: r.data.company_id, status: 'added' } : w) }));
        toast.success('Company added to ForesightRx — open it to enrich');
      } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add company'); }
      patchDisco(p => { const s = new Set(p.adding); s.delete(key); return { adding: s }; });
    },
  };

  const createList = async () => {
    setCreatingList(true);
    try {
      const r = await axios.post(`${API}/copilot/opportunity-lists`, { session_id: activeId, keys: [...disco.selected], name: listName }, { withCredentials: true });
      toast.success(`Opportunity list created · ${r.data.count} companies`);
      setListDialog(false);
      navigate(`/opportunity-lists/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create list'); }
    setCreatingList(false);
  };

  const latestDiscoveryId = [...messages].reverse().find(m => m.role === 'assistant' && m.discovery)?.id;

  const suggestions = [
    'Looking for companies/products in the dry-powder inhalation space. Open to a technology platform + company JV model. An existing product with manufacturing back-up would also work.',
    'Client wants late-stage or marketed ophthalmology products available for in-licensing in Europe, ideally with own manufacturing.',
    'Phase 2 CNS assets with novel MoA',
    'Latest Pierre Fabre BD news',
  ];

  return (
    <div className="flex bg-slate-50" style={{ height: 'calc(100vh - 3.5rem)' }} data-testid="copilot-page">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-200 overflow-hidden bg-white border-r border-slate-200 flex flex-col shrink-0`}>
        <div className="p-3 border-b border-slate-200">
          <Button onClick={newChat} className="w-full bg-slate-900 text-white hover:bg-slate-800 gap-2 text-sm" data-testid="new-chat-btn">
            <MessageSquarePlus className="w-4 h-4" /> New chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {sessions.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No conversations yet</p>}
          {sessions.map(s => (
            <div key={s.id} className={`group flex items-center gap-1 rounded-md ${activeId === s.id ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
              <button
                onClick={() => setActiveId(s.id)}
                className="flex-1 text-left px-2 py-1.5 text-xs text-slate-700 truncate"
                data-testid={`session-${s.id}`}
              >
                {s.title || 'New chat'}
              </button>
              <button
                onClick={() => deleteSession(s.id)}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded mr-1 transition-all"
                data-testid={`delete-session-${s.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(s => !s)}
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            data-testid="toggle-sidebar"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <Sparkles className="w-4 h-4 text-sky-600" />
          <h1 className="text-sm font-bold text-slate-900">Research Copilot</h1>
          <span className="text-xs text-slate-400 hidden md:inline">BD opportunity discovery across ForesightRx companies, assets, platforms and the web</span>
          <Link to="/opportunity-lists" className="ml-auto inline-flex items-center gap-1 text-xs text-slate-600 hover:text-sky-700 border border-slate-200 rounded px-2 py-1 hover:bg-slate-50" data-testid="opportunity-lists-link">
            <ListChecks className="w-3.5 h-3.5" /> Opportunity Lists
          </Link>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-scroll">
          {messages.length === 0 && !sending && (
            <div className="max-w-2xl mx-auto mt-12">
              <div className="text-center mb-8">
                <Compass className="w-10 h-10 text-sky-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-slate-900 mb-1">What are you looking for?</h2>
                <p className="text-sm text-slate-500">Paste a client requirement in plain language — Copilot interprets it into a structured brief, searches every ForesightRx company, asset and platform, and returns a tiered, actionable universe you can refine, expand to the web and turn into an opportunity list.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(s)}
                    className="text-left text-xs text-slate-700 bg-white border border-slate-200 hover:border-sky-300 hover:bg-sky-50 rounded-lg px-3 py-2 transition-colors"
                    data-testid={`suggestion-${i}`}
                  >
                    {i < 2 && <span className="block text-[9px] uppercase tracking-wider font-bold text-sky-600 mb-0.5">Paste a requirement</span>}
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => <Message key={m.id || i} m={m} navigate={navigate} isLatestDiscovery={m.id === latestDiscoveryId} disco={disco} handlers={handlers} />)}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-4 py-3 bg-white border border-slate-200">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            </div>
          )}
        </div>

        {disco.selected.size > 0 && (
          <div className="px-4 py-2 border-t border-sky-200 bg-sky-50 flex items-center gap-3" data-testid="shortlist-bar">
            <ListChecks className="w-4 h-4 text-sky-700" />
            <span className="text-xs font-semibold text-slate-900">{disco.selected.size} compan{disco.selected.size === 1 ? 'y' : 'ies'} in BD shortlist</span>
            <button onClick={() => patchDisco(() => ({ selected: new Set() }))} className="text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5" data-testid="clear-shortlist"><X className="w-3 h-3" /> clear</button>
            <button onClick={handlers.exportExcel} disabled={disco.exporting} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-50" data-testid="download-shortlist-btn">
              <Download className="w-3.5 h-3.5" /> Download shortlist (Excel)
            </button>
            <Button size="sm" onClick={() => setListDialog(true)} className="bg-slate-900 text-white hover:bg-slate-800 text-xs h-8 gap-1" data-testid="create-opportunity-list-btn">
              <ListChecks className="w-3.5 h-3.5" /> Create Opportunity List
            </Button>
          </div>
        )}

        {listDialog && (
          <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center" onClick={() => setListDialog(false)}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-[420px] max-w-[92vw]" onClick={e => e.stopPropagation()} data-testid="create-list-dialog">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Create Opportunity List</h3>
              <p className="text-xs text-slate-500 mb-3">Preserves the original requirement, search brief, {disco.selected.size} selected companies with match rationale, evidence, BD contacts, availability and outreach status.</p>
              <Input value={listName} onChange={e => setListName(e.target.value)} placeholder="List name (optional) — e.g. DPI universe · Client X" className="text-sm mb-3" data-testid="list-name-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setListDialog(false)} className="text-xs" data-testid="cancel-list-btn">Cancel</Button>
                <Button size="sm" onClick={createList} disabled={creatingList} className="bg-slate-900 text-white hover:bg-slate-800 text-xs gap-1" data-testid="confirm-create-list-btn">
                  {creatingList ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />} Create
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 border-t border-slate-200 bg-white">
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask anything — paste a client requirement, describe an asset need, find companies/platforms, or research a partnering opportunity…"
              rows={2}
              className="flex-1 resize-none text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100 max-h-40"
              data-testid="chat-input"
            />
            <Button onClick={send} disabled={sending || !input.trim()} className="bg-slate-900 text-white hover:bg-slate-800 gap-2 h-10" data-testid="send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 text-center mt-2">Grounded in ForesightRx data · web expansion via Perplexity · Claude / Gemini interpretation. Evidence is quoted from records; missing data is shown as “Not identified”, never guessed.</p>
        </div>
      </div>
    </div>
  );
}
